export type ActionHandler = (name: string, target: HTMLElement, event: MouseEvent) => void | Promise<void>;
export type RoutedActionHandler = (name: string, target: HTMLElement) => boolean | void | Promise<boolean | void>;

export function createActionRouter(...handlers: RoutedActionHandler[]): (name: string, target: HTMLElement) => Promise<boolean> {
  return async (name, target) => {
    target.closest<HTMLDetailsElement>(".topbar-more")?.removeAttribute("open");
    for (const handler of handlers) if (await handler(name, target)) return true;
    return false;
  };
}

export function bindActions(root: ParentNode, handler: ActionHandler, selector = "[data-action]"): void {
  root.querySelectorAll<HTMLElement>(selector).forEach(target => {
    target.addEventListener("click", event => {
      const name = target.dataset.action;
      if (name) {
        target.closest<HTMLDetailsElement>("details")?.removeAttribute("open");
        void handler(name, target, event);
      }
    });
  });
  bindMenuKeyboard(root);
}

export function bindMenuDismissal(root: Document): void {
  const selector = "details.composer-menu[open], details.kanban-card__actions[open]";
  root.addEventListener("click", event => {
    const target = event.target as Element | null;
    const containingMenu = target?.closest<HTMLDetailsElement>("details.composer-menu, details.kanban-card__actions") || null;
    root.querySelectorAll<HTMLDetailsElement>(selector).forEach(menu => {
      if (menu !== containingMenu) menu.open = false;
    });
  });
  root.addEventListener("keydown", event => {
    if (event.key !== "Escape") return;
    const menus = Array.from(root.querySelectorAll<HTMLDetailsElement>(selector));
    const menu = menus.at(-1);
    if (!menu) return;
    event.preventDefault();
    menu.open = false;
    menu.querySelector<HTMLElement>("summary")?.focus();
  });
}

function bindMenuKeyboard(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>("[role=menu]").forEach(menu => {
    if (menu.dataset.keyboardBound) return;
    menu.dataset.keyboardBound = "true";
    menu.addEventListener("keydown", event => {
      const items = Array.from(menu.querySelectorAll<HTMLElement>("[role=menuitem]:not([disabled])"))
        .filter(item => !item.closest("[hidden]") && item.ownerDocument.defaultView?.getComputedStyle(item).display !== "none");
      const current = items.indexOf(event.target as HTMLElement);
      if ((event.key === "Home" || event.key === "End") && items.length) {
        event.preventDefault(); event.stopPropagation();
        items[event.key === "Home" ? 0 : items.length - 1].focus();
      }
      if ((event.key === "ArrowDown" || event.key === "ArrowUp") && items.length) {
        event.preventDefault();
        event.stopPropagation();
        const offset = event.key === "ArrowDown" ? 1 : -1;
        items[(current + offset + items.length) % items.length].focus();
      }
      if (event.key === "Escape") {
        const details = menu.closest<HTMLDetailsElement>("details");
        if (details) {
          event.preventDefault();
          details.removeAttribute("open");
          details.querySelector<HTMLElement>("summary")?.focus();
        }
      }
    });
  });
}
